import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import type { DocumentNode } from "@apollo/client";
import Button from "@/components/Button";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import Dialog from "@/components/Dialog";
import {
  AlgoliaQuickSearch,
  type FacilityHit,
  createFacilityOnlySearchUiState,
  getFacilityDisplayName,
  isFacilityExactMatch,
  renderFacilityOnlyHit
} from "@/components/AlgoliaSearch";
import { NotePriorityValues } from "@/types";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { FACILITY_GOVERNANCE_TITLES_QUERY } from "@/pages/facilities/[facilityId]/people/governance/queries";
import {
  buildGovernanceTitleOptions,
  getGovernanceTitleFieldState,
  GOVERNANCE_ROLE_NOTE_TITLE
} from "@/pages/facilities/[facilityId]/people/governance/_utils";
import { CREATE_FACILITY_GOVERNANCE_ROLE_MUTATION } from "@/pages/facilities/[facilityId]/people/governance/mutations";

const facilitySearchIndexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;

interface PersonAddGovernanceRoleModalProps {
  open: boolean;
  personId: string;
  onClose: () => void;
  refetchQueries?: DocumentNode[];
}

interface FormValues {
  facilityId: string;
  governanceTitleId: string;
  internalNote: string;
}

export function PersonAddGovernanceRoleModal({
  open,
  personId,
  onClose,
  refetchQueries = []
}: PersonAddGovernanceRoleModalProps) {
  const [facilitySearchQuery, setFacilitySearchQuery] = useState("");
  const [createFacilityGovernanceRole, { loading: saving }] = useMutation(CREATE_FACILITY_GOVERNANCE_ROLE_MUTATION, {
    refetchQueries
  });
  const {
    data: titlesData,
    loading: titlesLoading,
    error: titlesError
  } = useQuery(FACILITY_GOVERNANCE_TITLES_QUERY, { fetchPolicy: "cache-first" });
  const governanceTitleOptions = useMemo(
    () => buildGovernanceTitleOptions(titlesData?.facilityGovernanceTitles),
    [titlesData?.facilityGovernanceTitles]
  );
  const titleFieldState = getGovernanceTitleFieldState({
    loading: titlesLoading,
    hasError: Boolean(titlesError),
    optionCount: governanceTitleOptions.length
  });

  const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      facilityId: "",
      governanceTitleId: "",
      internalNote: ""
    }
  });

  const selectedFacilityId = watch("facilityId");
  const selectedGovernanceTitleId = watch("governanceTitleId");

  useEffect(() => {
    if (!selectedGovernanceTitleId && governanceTitleOptions.length > 0) {
      setValue("governanceTitleId", governanceTitleOptions[0].value);
    }
  }, [governanceTitleOptions, selectedGovernanceTitleId, setValue]);

  const handleClose = () => {
    reset();
    setFacilitySearchQuery("");
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    if (!personId) {
      return;
    }

    const content = values.internalNote.trim();

    try {
      const { data } = await createFacilityGovernanceRole({
        variables: {
          input: {
            personId,
            facilityId: values.facilityId,
            facilityGovernanceTitleId: values.governanceTitleId,
            note: content ? { content, priority: NotePriorityValues.NONE, title: GOVERNANCE_ROLE_NOTE_TITLE } : null
          }
        }
      });

      if (!data?.createFacilityGovernanceRole.success) {
        showErrorSnackbar(data?.createFacilityGovernanceRole.message || "Unable to add governance role.");
        return;
      }

      showSnackbar("Governance role added");
      handleClose();
    } catch {
      showErrorSnackbar("Unable to add governance role.");
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Add Governance Role"
      className="w-[640px]"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={saving || !personId || !selectedFacilityId || !selectedGovernanceTitleId}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <p className="text-body-medium text-sys-on-surface">
          Search for a facility and assign a governance role for this person.
        </p>

        <Controller
          name="facilityId"
          control={control}
          rules={{ required: "Facility is required" }}
          render={({ fieldState }) => (
            <AlgoliaQuickSearch<FacilityHit>
              indexName={facilitySearchIndexName}
              label="Facility"
              placeholder="Search by facility name or ID"
              defaultValue={facilitySearchQuery}
              initialUiState={createFacilityOnlySearchUiState(facilitySearchIndexName)}
              renderHit={renderFacilityOnlyHit}
              getDisplayValue={getFacilityDisplayName}
              isExactMatch={isFacilityExactMatch}
              required
              error={fieldState.invalid}
              errorText={fieldState.error?.message}
              noResultsMessage="No facilities found"
              onSelect={(hit) => {
                setFacilitySearchQuery(getFacilityDisplayName(hit));
                setValue("facilityId", hit.facility_id, { shouldValidate: true });
              }}
              onClear={() => {
                setFacilitySearchQuery("");
                setValue("facilityId", "");
              }}
            />
          )}
        />

        <Controller
          name="governanceTitleId"
          control={control}
          rules={{ required: "Role is required" }}
          render={({ field: { onChange, value }, fieldState }) => (
            <Select
              label="Role"
              options={governanceTitleOptions}
              value={value}
              onSelect={onChange}
              disabled={titleFieldState.disabled}
              error={fieldState.invalid || Boolean(titleFieldState.errorText)}
              errorText={titleFieldState.errorText ?? fieldState.error?.message}
              supportingText={titleFieldState.supportingText}
              required
            />
          )}
        />

        <Controller
          name="internalNote"
          control={control}
          render={({ field: { onChange, value } }) => (
            <TextField
              type="textarea"
              rows={3}
              label="Internal Note (Optional)"
              value={value}
              onChangeText={onChange}
              className="w-full"
            />
          )}
        />
      </div>
    </Dialog>
  );
}
